/**
 * This file contains zod type equivalents for all of the runtype type
 * brands that we have in hex.
 *
 * TODO(EDT-190): Find a way to programatically map the database Ids to zod types
 * instead of writing these manually.
 */
import { z } from "zod";

import {
  AgentChatThreadId,
  AiAgentId,
  AppSessionCellId,
  AppSessionFileId,
  AppSessionId,
  BlockCellId,
  CanvasElementId,
  CanvasLayoutId,
  CategoryId,
  CellGroupCellId,
  CellId,
  ChartCellId,
  ChartCellSessionStateId,
  CodeCellId,
  CollapsibleCellId,
  CommentId,
  CommentReactionId,
  ComponentImportCellId,
  ContextChangesetId,
  DataAppElementId,
  DataAppFileId,
  DataConnectionHexVersionLinkId,
  DataConnectionId,
  DataSourceDbtMetricId,
  DataSourceTableId,
  DbtMetricCellId,
  DisplayTableCellId,
  DisplayTableColumnId,
  DisplayTableConfigId,
  DisplayTableSessionStateId,
  EditSessionBranchId,
  ExploreCellId,
  ExploreId,
  ExploreSpecSessionStateId,
  ExternalFileIntegrationHexVersionLinkId,
  ExternalFileIntegrationId,
  FileHexVersionLinkId,
  FileId,
  FilterCellId,
  FilterCellSessionStateId,
  GridColumnId,
  GridElementId,
  GridLayoutId,
  GridRowId,
  GroupId,
  HexId,
  HexVersionId,
  HexVersionSecretId,
  KernelSourceImageId,
  MagicEventId,
  MapCellId,
  MarkdownCellId,
  MetricCellId,
  OrgGuideFileId,
  OrgGuideFilePath,
  OrgGuideFileVersionId,
  OrgId,
  OrgUserFacingId,
  OutputContentId,
  OutputId,
  ParameterId,
  ParameterValueId,
  PivotCellId,
  ReviewId,
  ReviewRequestId,
  ReviewRequestLinkId,
  SecretId,
  SemanticDatasetId,
  SemanticProjectFileId,
  SemanticProjectFilePath,
  SemanticProjectId,
  SemanticViewId,
  SharedFilterId,
  SharedFilterSessionStateId,
  SqlCellId,
  StaticCellId,
  StaticGridLayoutId,
  StatusId,
  StoryElementId,
  SyncRepositoryHexVersionLinkId,
  SyncRepositoryId,
  TaoProjectId,
  TaskId,
  TextCellId,
  UserId,
  VcsPackageHexVersionLinkId,
  VcsPackageId,
  VegaChartCellId,
  WritebackCellId,
} from "../idTypeBrands.js";

/**
 * A branded id stored as a `uuid` column, so the format is validated as well as
 * branded. Only for ids that really are uuids in the database: applying it to an id
 * held in a `text` column would start rejecting values that column allows. `OrgId` and
 * `DisplayTableColumnId` are the two that are not uuids — see the section below.
 *
 * Every brand has a named schema — the ids here, and the rest alongside their own
 * brand declarations — so this is only for a brand reached through a type parameter,
 * where no named schema can be named.
 */
export const zUuidBrandedString = <BRAND extends string>(): z.ZodEffects<
  z.ZodString,
  BRAND,
  string
> =>
  z
    .string()
    .uuid()
    .transform((val) => val as BRAND);

// ---------------------------------------------------------------------------
// UUIDs
// ---------------------------------------------------------------------------
export const zAgentChatThreadId = zUuidBrandedString<AgentChatThreadId>();
export const zAppSessionCellId = zUuidBrandedString<AppSessionCellId>();
export const zAppSessionFileId = zUuidBrandedString<AppSessionFileId>();
export const zAppSessionId = zUuidBrandedString<AppSessionId>();
export const zBlockCellId = zUuidBrandedString<BlockCellId>();
export const zCanvasElementId = zUuidBrandedString<CanvasElementId>();
export const zCanvasLayoutId = zUuidBrandedString<CanvasLayoutId>();
export const zCategoryId = zUuidBrandedString<CategoryId>();
export const zCellGroupCellId = zUuidBrandedString<CellGroupCellId>();
export const zCellId = zUuidBrandedString<CellId>();
export const zChartCellId = zUuidBrandedString<ChartCellId>();
export const zChartCellSessionStateId =
  zUuidBrandedString<ChartCellSessionStateId>();
export const zCodeCellId = zUuidBrandedString<CodeCellId>();
export const zCollapsibleCellId = zUuidBrandedString<CollapsibleCellId>();
export const zCommentId = zUuidBrandedString<CommentId>();
export const zCommentReactionId = zUuidBrandedString<CommentReactionId>();
export const zComponentImportCellId =
  zUuidBrandedString<ComponentImportCellId>();
export const zContextChangesetId = zUuidBrandedString<ContextChangesetId>();
export const zDataAppElementId = zUuidBrandedString<DataAppElementId>();
export const zDataAppFileId = zUuidBrandedString<DataAppFileId>();
export const zDataConnectionHexVersionLinkId =
  zUuidBrandedString<DataConnectionHexVersionLinkId>();
export const zDataConnectionId = zUuidBrandedString<DataConnectionId>();
export const zDataSourceDbtMetricId =
  zUuidBrandedString<DataSourceDbtMetricId>();
export const zDataSourceTableId =
  zUuidBrandedString<DataSourceTableId>().describe(
    "A tableId (uuid) for a table.",
  );
export const zDbtMetricCellId = zUuidBrandedString<DbtMetricCellId>();
export const zDisplayTableCellId = zUuidBrandedString<DisplayTableCellId>();
export const zDisplayTableConfigId = zUuidBrandedString<DisplayTableConfigId>();
export const zDisplayTableSessionStateId =
  zUuidBrandedString<DisplayTableSessionStateId>();
export const zEditSessionBranchId = zUuidBrandedString<EditSessionBranchId>();
export const zExploreCellId = zUuidBrandedString<ExploreCellId>();
export const zExploreId = zUuidBrandedString<ExploreId>();
export const zExploreSpecSessionStateId =
  zUuidBrandedString<ExploreSpecSessionStateId>();
export const zExternalFileIntegrationHexVersionLinkId =
  zUuidBrandedString<ExternalFileIntegrationHexVersionLinkId>();
export const zExternalFileIntegrationId =
  zUuidBrandedString<ExternalFileIntegrationId>();
export const zFileHexVersionLinkId = zUuidBrandedString<FileHexVersionLinkId>();
export const zFileId = zUuidBrandedString<FileId>();
export const zFilterCellId = zUuidBrandedString<FilterCellId>();
export const zFilterCellSessionStateId =
  zUuidBrandedString<FilterCellSessionStateId>();
export const zGridColumnId = zUuidBrandedString<GridColumnId>();
export const zGridElementId = zUuidBrandedString<GridElementId>();
export const zGridLayoutId = zUuidBrandedString<GridLayoutId>();
export const zGridRowId = zUuidBrandedString<GridRowId>();
export const zGroupId = zUuidBrandedString<GroupId>();
export const zHexId = zUuidBrandedString<HexId>();
export const zHexVersionId = zUuidBrandedString<HexVersionId>();
export const zHexVersionSecretId = zUuidBrandedString<HexVersionSecretId>();
export const zKernelSourceImageId = zUuidBrandedString<KernelSourceImageId>();
export const zMagicEventId = zUuidBrandedString<MagicEventId>();
export const zMapCellId = zUuidBrandedString<MapCellId>();
export const zMarkdownCellId = zUuidBrandedString<MarkdownCellId>();
export const zMetricCellId = zUuidBrandedString<MetricCellId>();
export const zOrgGuideFileId = zUuidBrandedString<OrgGuideFileId>();
export const zOrgGuideFileVersionId =
  zUuidBrandedString<OrgGuideFileVersionId>();
export const zOutputContentId = zUuidBrandedString<OutputContentId>();
export const zOutputId = zUuidBrandedString<OutputId>();
export const zParameterId = zUuidBrandedString<ParameterId>();
export const zParameterValueId = zUuidBrandedString<ParameterValueId>();
export const zPivotCellId = zUuidBrandedString<PivotCellId>();
export const zReviewId = zUuidBrandedString<ReviewId>();
export const zReviewRequestId = zUuidBrandedString<ReviewRequestId>();
export const zReviewRequestLinkId = zUuidBrandedString<ReviewRequestLinkId>();
export const zSecretId = zUuidBrandedString<SecretId>();
export const zSemanticDatasetId = zUuidBrandedString<SemanticDatasetId>();
export const zSemanticProjectFileId =
  zUuidBrandedString<SemanticProjectFileId>();
export const zSemanticProjectId = zUuidBrandedString<SemanticProjectId>();
export const zSemanticViewId = zUuidBrandedString<SemanticViewId>();
export const zSharedFilterId = zUuidBrandedString<SharedFilterId>();
export const zSharedFilterSessionStateId =
  zUuidBrandedString<SharedFilterSessionStateId>();
export const zSqlCellId = zUuidBrandedString<SqlCellId>();
export const zStaticCellId = zUuidBrandedString<StaticCellId>();
export const zStaticGridLayoutId = zUuidBrandedString<StaticGridLayoutId>();
export const zStatusId = zUuidBrandedString<StatusId>();
export const zStoryElementId = zUuidBrandedString<StoryElementId>();
export const zSyncRepositoryHexVersionLinkId =
  zUuidBrandedString<SyncRepositoryHexVersionLinkId>();
export const zSyncRepositoryId = zUuidBrandedString<SyncRepositoryId>();
export const zTaoProjectId = zUuidBrandedString<TaoProjectId>();
export const zTaskId = zUuidBrandedString<TaskId>();
export const zTextCellId = zUuidBrandedString<TextCellId>();
export const zUserId = zUuidBrandedString<UserId>();
export const zVcsPackageHexVersionLinkId =
  zUuidBrandedString<VcsPackageHexVersionLinkId>();
export const zVcsPackageId = zUuidBrandedString<VcsPackageId>();
export const zVegaChartCellId = zUuidBrandedString<VegaChartCellId>();
export const zWritebackCellId = zUuidBrandedString<WritebackCellId>();

// ---------------------------------------------------------------------------
// Other IDs (may be arbitrary strings)
// ---------------------------------------------------------------------------
export const zOrgId = z.string().transform((val) => val as OrgId);

export const zOrgUserFacingId = z
  .string()
  .transform((val) => val as OrgUserFacingId);

export const zOrgGuideFilePath = z
  .string()
  .transform((val) => val as OrgGuideFilePath);

export const zSemanticProjectFilePath = z
  .string()
  .transform((val) => val as SemanticProjectFilePath);

const OUTPUT_NAME_DESCRIPTION =
  "The name of the output variable for this cell. Must be lowercase snake_case (matching `^[a-z_][a-z0-9_]*$`) and unique across all cells. When set, the cell's result is accessible as a named dataframe.";
export const zOutputName = z
  .string()
  .regex(/^[a-z_][a-z0-9_]*$/, "Must be lowercase snake_case.")
  .describe(OUTPUT_NAME_DESCRIPTION);
export const zOptionalOutputName = zOutputName
  .describe(`Optional. ${OUTPUT_NAME_DESCRIPTION}`)
  .nullish();

export const zAiAgentId = z.string().transform((val) => val as AiAgentId);

export const zDisplayTableColumnId = z
  .string()
  .transform((val) => val as DisplayTableColumnId);
